import React from 'react';
import { Trash2, ShoppingCart } from 'lucide-react';
import { useCurrency } from '../../hooks/useCurrency';
import type { CartItem, CartSummary, DiscountMode } from '../../types/pharmacy-pos';

interface CartProps {
  items: CartItem[];
  discountMode: DiscountMode;
  discountValue: number;
  vatPercent: number;
  onQuantityChange: (index: number, quantity: number) => void;
  onRemoveItem: (index: number) => void;
  onDiscountModeChange: (mode: DiscountMode) => void;
  onDiscountValueChange: (value: number) => void;
  onVatPercentChange: (percent: number) => void;
  disabled?: boolean;
}

export const Cart: React.FC<CartProps> = ({
  items,
  discountMode,
  discountValue,
  vatPercent,
  onQuantityChange,
  onRemoveItem,
  onDiscountModeChange,
  onDiscountValueChange,
  onVatPercentChange,
  disabled = false
}) => {
  const { formatCurrency } = useCurrency();
  
  const calculateSummary = (): CartSummary => {
    const subtotal = items.reduce((sum, item) => sum + item.line_total, 0);
    
    let discount_amount = 0;
    if (discountMode === 'percent') {
      discount_amount = subtotal * (discountValue / 100);
    } else {
      discount_amount = discountValue;
    }

    const vat_amount = (subtotal - discount_amount) * (vatPercent / 100);
    const grand_total = subtotal - discount_amount + vat_amount;

    return {
      subtotal,
      discount_amount,
      vat_amount,
      grand_total
    };
  };

  const summary = calculateSummary();

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full flex flex-col">
      {/* Empty State */}
      {items.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <ShoppingCart className="w-8 h-8 text-green-600" />
          </div>
          <p className="text-green-600 font-medium text-lg mb-2">No Medicine added in 'Medicine Order List'</p>
          <p className="text-gray-500 text-sm">Add medicines from the table to start creating an order</p>
        </div>
      ) : (
        <>
          {/* Cart Items */}
          <div className="flex-1 overflow-auto p-4 space-y-2">
            {items.map((item, index) => (
              <div
                key={index}
                className="bg-gray-50 border border-gray-200 rounded-lg p-3 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 text-sm">{item.medicine.name}</div>
                    {item.medicine.generic_name && (
                      <div className="text-xs text-gray-500">{item.medicine.generic_name}</div>
                    )}
                    {item.medicine.batch_no && (
                      <div className="text-xs text-gray-500">Batch: {item.medicine.batch_no}</div>
                    )}
                  </div>
                  <button
                    onClick={() => onRemoveItem(index)}
                    disabled={disabled}
                    className="text-red-600 hover:text-red-800 disabled:opacity-50 ml-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-600">Qty:</span>
                    <input
                      type="number"
                      min="1"
                      max={item.medicine.stock}
                      value={item.quantity}
                      onChange={(e) => onQuantityChange(index, parseInt(e.target.value) || 1)}
                      disabled={disabled}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
                    />
                    <span className="text-xs text-gray-500">× {item.unit_price.toFixed(2)}</span>
                  </div>
                  <div className="text-sm font-semibold text-gray-900">
                    {formatCurrency(item.line_total)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Summary Section */}
          <div className="border-t border-gray-200 p-4 space-y-3 bg-gray-50">
            {/* Discount */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Discount</label>
              <div className="flex space-x-2">
                <select
                  value={discountMode}
                  onChange={(e) => onDiscountModeChange(e.target.value as DiscountMode)}
                  disabled={disabled}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
                >
                  <option value="percent">%</option>
                  <option value="flat">Flat</option>
                </select>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={discountValue}
                  onChange={(e) => onDiscountValueChange(parseFloat(e.target.value) || 0)}
                  disabled={disabled}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* VAT */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">VAT (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={vatPercent}
                onChange={(e) => onVatPercentChange(parseFloat(e.target.value) || 0)}
                disabled={disabled}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
                placeholder="0.00"
              />
            </div>

            {/* Totals */}
            <div className="pt-3 border-t border-gray-300 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium text-gray-900">{formatCurrency(summary.subtotal)}</span>
              </div>
              
              {summary.discount_amount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Discount:</span>
                  <span className="font-medium text-red-600">-{formatCurrency(summary.discount_amount)}</span>
                </div>
              )}
              
              {summary.vat_amount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">VAT ({vatPercent}%):</span>
                  <span className="font-medium text-gray-900">{formatCurrency(summary.vat_amount)}</span>
                </div>
              )}
              
              <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-300">
                <span className="text-gray-900">Grand Total:</span>
                <span className="text-green-600">{formatCurrency(summary.grand_total)}</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
